// Apļa ģeometrija (polārās koordinātas SVG telpā, 0° = pa labi, −90° = augšā).

import type { Index } from '@balansa/shared';

export const R = 270;
export const RA = R + 3;
export const B0 = R + 8;
export const B1 = R + 34;
export const H0 = R + 40;
export const H1 = R + 50;

export const f2 = (n: number) => String(Math.round(n * 100) / 100);
export const rad = (d: number) => (d * Math.PI) / 180;
export const polar = (a: number, r: number): [number, number] => [r * Math.cos(rad(a)), r * Math.sin(rad(a))];
export const pt = (p: [number, number]) => f2(p[0]) + ' ' + f2(p[1]);
export const normAng = (a: number) => (((a % 360) + 540) % 360) - 180;

export interface Sector {
  id: string;
  c: number;
  a0: number;
  a1: number;
}
export interface Axis {
  id: string;
  angle: number;
}
export interface Layout {
  main: boolean;
  step: number;
  sectors: Sector[];
  axes: Axis[];
  axisAngle: Map<string, number>;
  anchor: (id: string) => number | undefined;
}

/** Galvenajā skatā asis ir virsjomas, detalizētajā — visas lapas (apakšjomas). */
export function layout(idx: Index, view: 'main' | 'detail'): Layout {
  const main = view === 'main';
  const N = idx.tops.length;
  const step = 360 / Math.max(N, 1);
  const sectors = idx.tops.map((id, i) => {
    const c = -90 + i * step;
    return { id, c, a0: c - step / 2, a1: c + step / 2 };
  });
  const axes: Axis[] = [];
  if (main) sectors.forEach((s) => axes.push({ id: s.id, angle: s.c }));
  else
    sectors.forEach((s) => {
      const lv = idx.leaves.get(s.id)!;
      lv.forEach((l, j) => axes.push({ id: l, angle: s.a0 + ((j + 0.5) * step) / lv.length }));
    });
  const axisAngle = new Map(axes.map((a) => [a.id, a.angle]));
  const anchor = (id: string) => {
    if (main) return axisAngle.get(idx.top.get(id)!);
    const lv = idx.leaves.get(id);
    if (!lv || !lv.length) return undefined;
    return lv.reduce((s, l) => s + (axisAngle.get(l) ?? 0), 0) / lv.length;
  };
  return { main, step, sectors, axes, axisAngle, anchor };
}

export function wedge(r: number, a0: number, a1: number) {
  if (a1 - a0 >= 359.9) return `M${-r} 0A${r} ${r} 0 1 1 ${r} 0A${r} ${r} 0 1 1 ${-r} 0Z`;
  return 'M0 0L' + pt(polar(a0, r)) + 'A' + r + ' ' + r + ' 0 ' + (a1 - a0 > 180 ? 1 : 0) + ' 1 ' + pt(polar(a1, r)) + 'Z';
}

export function annular(r0: number, r1: number, a0: number, a1: number) {
  const lg = a1 - a0 > 180 ? 1 : 0;
  return (
    'M' + pt(polar(a0, r1)) + 'A' + r1 + ' ' + r1 + ' 0 ' + lg + ' 1 ' + pt(polar(a1, r1)) +
    'L' + pt(polar(a1, r0)) + 'A' + r0 + ' ' + r0 + ' 0 ' + lg + ' 0 ' + pt(polar(a0, r0)) + 'Z'
  );
}

/** Loka ceļš tekstam — apakšējā pusē apgriezts, lai teksts nebūtu otrādi. */
export function arcPath(r: number, a0: number, a1: number) {
  const c = normAng((a0 + a1) / 2);
  const lg = a1 - a0 > 180 ? 1 : 0;
  if (c > 1 && c < 179) return 'M' + pt(polar(a1, r)) + 'A' + r + ' ' + r + ' 0 ' + lg + ' 0 ' + pt(polar(a0, r));
  return 'M' + pt(polar(a0, r)) + 'A' + r + ' ' + r + ' 0 ' + lg + ' 1 ' + pt(polar(a1, r));
}

/** Samazina burtu izmēru un, ja vajag, saīsina tekstu, lai tas ietilptu lokā. */
export function fitLabel(t: string, maxLen: number, base: number) {
  const w = (s: string, z: number) => s.length * z * 0.58;
  let z = base;
  while (z > 9 && w(t, z) > maxLen) z -= 0.5;
  if (w(t, z) <= maxLen) return { text: t, size: z };
  let s = t;
  while (s.length > 2 && w(s + '…', z) > maxLen) s = s.slice(0, -1);
  return { text: s + '…', size: z };
}
